/**
 * The Labyrinth Studios brand mark and the loading states built from it.
 *
 * - BrandMark    — the maze mark as inline SVG (static, fully drawn).
 * - BrandSplash  — full-screen loading state: the full mark + wordmark lockup
 *                  plus activity dots. Shown while the auth session is being
 *                  checked. The pre-React splash in index.html plays the
 *                  "draw-in" animation; by the time this component renders the
 *                  lockup is already drawn, so it stays static here and the
 *                  handoff reads as seamless.
 * - PageLoader   — small pulsing mark used as the route-chunk Suspense fallback.
 *
 * Geometry is the real vector artwork from public/logo-lockup.svg (Asset 44):
 * the maze as individual stroke paths and LABYRINTH STUDIOS as outlined Aaux
 * Next letterforms. The mark path data is duplicated in index.html (the pre-boot
 * splash can't import this file) — if the artwork changes, update both.
 */

import { useEffect, useRef } from 'react';

// Maze strokes. pathLength=1 lets CSS animate a draw-in with stroke-dasharray:1
// regardless of each path's true length. Coordinates share the lockup's
// 0 0 141.15 186.15 viewBox; the mark occupies the top 0..141.15 band.
export const MARK_PATHS = [
  'M72.25,70c0-23.19,0-46.71,0-70',
  'M123.75,69.49c-17.11,0-34.82,0-52,0',
  'M92.71,41.45c0,18.81,0,39.16,0,58',
  'M60.21,36.95c10.69.02,22.31.05,33,.07',
  'M87.71,49.45c0,19.49-.02,40.52-.02,60',
  'M67.21,43.95c6.68,0,14.32,0,21,0',
  'M97.71,31.45c0,19.76,0,41.22,0,61',
  'M54.21,27.95c14.26,0,29.72,0,44,0',
  'M103.71,24.45c0,19.76,0,41.22,0,61',
  'M51.21,20.95c16.89,0,35.13,0,52,0',
  'M68.89,71.15c0,23.19,0,46.71,0,70',
  'M17.39,71.65c17.11,0,34.82,0,52,0',
  'M48.44,99.69c0-18.81,0-39.16,0-58',
  'M80.94,104.19c-10.69-.02-22.31-.05-33-.07',
  'M53.44,91.69c0-19.49.02-40.52.02-60',
  'M73.94,97.19c-6.68,0-14.32,0-21,0',
  'M43.44,109.69c0-19.76,0-41.22,0-61',
  'M86.94,113.19c-14.26,0-29.72,0-44,0',
  'M37.44,116.69c0-19.76,0-41.22,0-61',
  'M89.94,120.19c-16.89,0-35.13,0-52,0',
];

// LABYRINTH STUDIOS as outlined Aaux Next letterforms (filled). Sits below the
// mark in the shared viewBox (y ≈ 150..186).
const WORDMARK_PATHS = [
  'M19.76,165.25h-8.93v-15.36h1.36v14.08h7.57v1.28Z',
  'M34.53,165.25h-1.45l-1.74-4.55h-7l-1.72,4.55h-1.47l5.9-15.36h1.58l5.9,15.36ZM30.87,159.48l-3.04-8.01-3.04,8.01h6.07Z',
  'M42.14,165.25h-5.68v-15.36h5.28c2.84,0,5.04,1.1,5.04,4.09,0,1.23-.57,2.53-2.13,3.32,1.96.68,2.73,2.16,2.73,3.7,0,3.15-2.29,4.25-5.24,4.25ZM41.76,151.19h-3.94v5.65h4.05c1.91,0,3.56-.53,3.56-2.84s-1.83-2.82-3.67-2.82ZM42.14,158.01h-4.31v5.94h4.31c1.96,0,3.87-.48,3.87-2.95s-1.87-2.99-3.87-2.99Z',
  'M59.19,149.89l-5.08,10.28v5.08h-1.39v-5.08l-5.08-10.28h1.54l4.25,8.71,4.25-8.71h1.52Z',
  'M67.5,158.16c.48.33.88.88,1.21,1.43l3.43,5.65h-1.61l-3.26-5.48c-.46-.77-.99-1.5-2.11-1.5h-2.4v6.98h-1.36v-15.36h5.28c2.88,0,4.95,1.23,4.95,4.22,0,2.31-1.43,3.72-4.14,4.05ZM66.69,151.23h-3.92v5.81h3.92c1.98,0,3.56-.66,3.56-2.93s-1.63-2.88-3.56-2.88Z',
  'M76.43,165.25h-1.39v-15.36h1.39v15.36Z',
  'M90.65,165.25h-1.32l-7.92-12.81v12.81h-1.36v-15.36h1.34l7.9,12.81v-12.81h1.36v15.36Z',
  'M103.96,151.23h-4.88v14.02h-1.36v-14.02h-4.88v-1.34h11.13v1.34Z',
  'M117.27,165.25h-1.39v-7.31h-8.38v7.31h-1.36v-15.36h1.36v6.78h8.38v-6.78h1.39v15.36Z',
  'M49.5,185.87c-3.08,0-5.24-1.15-5.92-3.98l1.32-.24c.55,2.24,2.57,2.93,4.6,2.93,1.87,0,4.05-.97,4.05-3.19,0-4.58-9.44-1.9-9.44-7.31,0-2.71,2.44-3.92,4.93-3.91,2.46,0,4.2.86,5.3,2.99l-1.21.55c-.88-1.67-2.22-2.25-4.09-2.25-1.52,0-3.54.59-3.54,2.62,0,3.98,9.44,1.5,9.44,7.31,0,2.93-2.66,4.49-5.44,4.49Z',
  'M67.81,171.66h-4.88s0,14.01,0,14.01h-1.36s0-14.02,0-14.02h-4.88s0-1.34,0-1.34h11.13s0,1.35,0,1.35Z',
  'M81.89,179.17c0,3.94-1.34,6.71-5.57,6.71-4.2,0-5.57-2.89-5.56-6.71v-8.85s1.39,0,1.39,0v8.8c0,2.77.66,5.39,4.18,5.39,3.54,0,4.2-2.6,4.2-5.39v-8.8s1.37,0,1.37,0v8.85Z',
  'M90.33,185.68h-4.38s0-15.36,0-15.36h4.38c6.09,0,6.64,2.42,6.64,7.68,0,5.24-.55,7.68-6.65,7.68ZM94.98,173.23c-.66-1.1-1.98-1.56-4.42-1.56h-3.23s0,12.65,0,12.65h3.23c2.44,0,3.76-.46,4.42-1.56.66-1.1.62-2.99.62-4.75,0-1.74.04-3.65-.61-4.77Z',
  'M102.52,185.69h-1.39s0-15.36,0-15.36h1.39s0,15.36,0,15.36Z',
  'M112.53,185.89c-4.58,0-6.12-3.85-6.11-7.88,0-4.03,1.54-7.88,6.12-7.87,4.58,0,6.14,3.83,6.14,7.88,0,4.03-1.57,7.88-6.14,7.88ZM112.53,171.5c-3.92,0-4.75,3.5-4.75,6.51,0,3.01.83,6.51,4.75,6.52,3.92,0,4.75-3.5,4.75-6.51,0-3.01-.83-6.51-4.75-6.51Z',
  'M127.47,185.9c-3.08,0-5.24-1.15-5.92-3.98l1.32-.24c.55,2.24,2.57,2.93,4.6,2.93,1.87,0,4.05-.97,4.05-3.19,0-4.58-9.44-1.9-9.44-7.31,0-2.71,2.44-3.92,4.93-3.91,2.46,0,4.2.86,5.3,2.99l-1.21.55c-.88-1.67-2.22-2.25-4.09-2.25-1.52,0-3.54.59-3.54,2.62,0,3.98,9.44,1.5,9.44,7.31,0,2.93-2.66,4.49-5.44,4.49Z',
];

/**
 * Just the maze mark. Its own square viewBox so it centres on its own.
 * With `animate`, the strokes trace themselves on (staggered) — the same
 * draw-in as the launch splash, driven by CSS (.brand-draw-mark in ui.css).
 * `drawOffsetMs` shifts the whole draw earlier (via negative animation-delay)
 * so a loader that replaces another mid-load picks the draw up where it left
 * off instead of restarting — see useDrawElapsed / PageLoader.
 */
export function BrandMark({
  size = 120,
  className,
  animate = false,
  drawOffsetMs = 0,
}: { size?: number; className?: string; animate?: boolean; drawOffsetMs?: number }) {
  return (
    <svg
      viewBox="0 0 141.15 141.15"
      width={size}
      height={size}
      className={[animate && 'brand-draw-mark', className].filter(Boolean).join(' ') || undefined}
      aria-hidden="true"
    >
      {MARK_PATHS.map((d, i) => (
        <path
          key={d}
          d={d}
          pathLength={1}
          fill="none"
          stroke="#000"
          strokeWidth={2.5}
          strokeMiterlimit={10}
          style={animate ? { animationDelay: `${i * 22 - drawOffsetMs}ms` } : undefined}
        />
      ))}
    </svg>
  );
}

/**
 * Coordinates the draw-in across back-to-back loaders so they read as ONE
 * continuous draw. On a slower page you get two loaders in a row — the
 * lazy-chunk Suspense fallback, then the page's own data-loading gate — and
 * each fresh mount would otherwise replay the animation from zero ("draws
 * halfway, restarts"). Instead, a loader that mounts while another was just on
 * screen resumes the draw at the current elapsed offset; a genuinely new
 * navigation (no loader on screen for a beat) starts a fresh draw.
 *
 * The module state is written during render behind a ref guard, so it runs
 * once per mount and stays correct under StrictMode's double-mount.
 */
const SEQUENCE_GAP_MS = 350;
let drawEpoch = 0;
let lastSeenAt = 0;

function useDrawElapsed(): number {
  const elapsedRef = useRef<number | null>(null);
  if (elapsedRef.current === null) {
    const now = Date.now();
    if (now - lastSeenAt > SEQUENCE_GAP_MS) drawEpoch = now; // fresh sequence
    lastSeenAt = now;
    elapsedRef.current = now - drawEpoch;
  }
  useEffect(() => {
    lastSeenAt = Date.now();
    const id = window.setInterval(() => { lastSeenAt = Date.now(); }, 100);
    return () => {
      window.clearInterval(id);
      lastSeenAt = Date.now();
    };
  }, []);
  return elapsedRef.current;
}

/**
 * The full mark + wordmark lockup, matching public/logo-lockup.svg.
 * With `animate`, the maze draws itself in stroke by stroke and the wordmark
 * fades up beneath it — driven by CSS (.brand-draw-lockup in ui.css).
 */
export function BrandLockup({
  width = 220,
  className,
  animate = false,
}: { width?: number; className?: string; animate?: boolean }) {
  return (
    <svg
      viewBox="0 0 141.15 186.15"
      width={width}
      height={width * (186.15 / 141.15)}
      className={[animate && 'brand-draw-lockup', className].filter(Boolean).join(' ') || undefined}
      aria-hidden="true"
    >
      <g className="mark" fill="none" stroke="#000" strokeWidth={2.2} strokeMiterlimit={10}>
        {MARK_PATHS.map((d, i) => (
          <path
            key={d}
            d={d}
            pathLength={1}
            style={animate ? { animationDelay: `${i * 45}ms` } : undefined}
          />
        ))}
      </g>
      <g className="word" fill="#000" stroke="#000" strokeWidth={0.5} strokeMiterlimit={10}>
        {WORDMARK_PATHS.map((d) => (
          <path key={d} d={d} />
        ))}
      </g>
    </svg>
  );
}

/** Full-screen branded loading state (auth check on app launch). */
export function BrandSplash() {
  return (
    <div className="brand-splash" role="status" aria-label="Loading Labyrinth">
      <BrandLockup width={220} animate />
      <div className="brand-splash__dots" aria-hidden="true">
        <span /><span /><span />
      </div>
    </div>
  );
}

/**
 * Branded loading state built from the pulsing mark. The single loading
 * indicator used across the app — as a page-level gate (default) and, with
 * `inline`, as a smaller loader inside a panel, modal, or sub-section.
 */
export function PageLoader({ inline = false }: { inline?: boolean }) {
  const drawOffsetMs = useDrawElapsed();
  return (
    <div
      className={inline ? 'ui-pageloader ui-pageloader--inline' : 'ui-pageloader'}
      role="status"
      aria-label="Loading"
    >
      <BrandMark
        size={inline ? 30 : 48}
        className="ui-pageloader__mark"
        animate
        drawOffsetMs={drawOffsetMs}
      />
    </div>
  );
}
